import { Button, Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi, Link } from '@tanstack/react-router';

import { CustomFieldsRenderer } from './CustomFieldsRenderer.js';
import { useInventoryQuery, useRestoreInventoryMutation } from './useInventoryQuery.js';

/**
 * The `/inventory/$id` item-detail route. Reads the `id` path param, reuses the coarse inventory
 * query (the item + its category's field definitions are already in that payload — no second
 * round-trip), and renders the item overview + quantity + the dynamic per-category custom fields
 * via `CustomFieldsRenderer` (which renders user-controlled values AS TEXT — T-22-xss).
 *
 * Loading → accessible `Spinner`; error / not-found → announced error state with a back link
 * (the reports 3-state contract). The archived filter is included so an archived item still
 * resolves on its detail.
 */

const routeApi = getRouteApi('/inventory/$id');

export function ItemDetailRoute() {
  const { t } = useTranslation();
  const { id } = routeApi.useParams();
  const itemId = Number(id);

  // Query BOTH active and archived so an item resolves regardless of its archived state.
  const query = useInventoryQuery({ archived: true });
  const restoreMutation = useRestoreInventoryMutation();

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  const item = query.isSuccess ? (query.data.items ?? []).find((i) => i.id === itemId) : undefined;
  const category = item
    ? (query.data?.categories ?? []).find((c) => c.id === item.categoryId)
    : undefined;

  if (query.isError || !item) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('inventory:error.detailHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('inventory:error.detailBody')}</p>
        <Button asChild variant="secondary">
          <Link to="/inventory">{t('inventory:actions.backToItems')}</Link>
        </Button>
      </section>
    );
  }

  const fieldDefinitions = category?.fieldDefinitions ?? [];
  const archived = Boolean(item.archivedAt);

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">{item.name}</h1>
          <p className="mt-1 text-muted-foreground text-sm">
            {t('inventory:detail.categoryLabel')}: {item.categoryName}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* The h1 beside these already prints the item name, so repeating it in the button label
              only lengthens it. It stays on the accessible name, where it costs a sighted reader
              nothing. */}
          {archived ? (
            <Button
              variant="secondary"
              disabled={restoreMutation.isPending}
              onClick={() => restoreMutation.mutate({ ids: [item.id] })}
              aria-label={t('inventory:actions.restoreItem', { name: item.name })}
            >
              {t('inventory:actions.restore')}
            </Button>
          ) : (
            <Button asChild>
              <Link
                to="/inventory/$id/edit"
                params={{ id: String(item.id) }}
                aria-label={t('inventory:actions.editItem', { name: item.name })}
              >
                {t('inventory:actions.edit')}
              </Link>
            </Button>
          )}
          <Button asChild variant="secondary">
            <Link to="/inventory">{t('inventory:actions.back')}</Link>
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <section className="rounded-md border border-border bg-card p-6 lg:col-span-1">
          <h2 className="font-display font-semibold text-foreground text-lg">
            {t('inventory:detail.quantityHeading')}
          </h2>
          <p className="mt-2 font-mono font-semibold text-2xl text-foreground">
            {item.quantity}
            {item.quantityUnit ? (
              <span className="ml-1 text-base text-muted-foreground">{item.quantityUnit}</span>
            ) : null}
          </p>
          {item.lowStock ? (
            <p className="mt-2 inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive text-xs">
              {t('inventory:lowStock.indicator')}
            </p>
          ) : null}
        </section>

        <section className="rounded-md border border-border bg-card p-6 lg:col-span-2">
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('inventory:detail.customFieldsHeading')}
          </h2>
          <CustomFieldsRenderer item={item} fieldDefinitions={fieldDefinitions} />

          <h3 className="mt-6 mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
            {t('inventory:detail.notesHeading')}
          </h3>
          <p className="text-foreground text-sm">
            {item.notes ? item.notes : t('inventory:detail.noNotes')}
          </p>
        </section>
      </div>
    </div>
  );
}
